import {
  AI_INSTRUCTION_MAX,
  connectorTitles,
  type Item,
  type ItemHistoryEntry,
  type ItemSourceLink,
  type ItemSuggestion,
  itemTypes,
  REPLY_BODY_MAX,
  type SuggestionConfidence,
  TITLE_MAX,
} from '@hugo/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  CheckIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  NotebookPenIcon,
  ReplyIcon,
  RotateCcwIcon,
  SendIcon,
  SparklesIcon,
  Trash2Icon,
  Undo2Icon,
  XIcon,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useIsMobile } from '@/hooks/use-mobile'
import { ApiError } from '@/lib/api'
import {
  choiceLabel,
  formatDue,
  formatDueDate,
  itemStateLabel,
  localDateInput,
  relativeTime,
  sourceLabel,
} from '@/lib/format'
import { historyLine, undoableHugoFiling } from '@/lib/history'
import { useItemActions } from '@/lib/item-actions'
import { queryKeys, useAiStatus, useItemDetail, useRunTool, useSettings } from '@/lib/items'
import { WAITING_CHOICES, type WaitingKind } from '@/lib/lists'
import { log } from '@/lib/log'
import { PRIORITY_CHOICES, priorityLabel } from '@/lib/priority'
import { conditionLabel, ruleAt, TYPE_LABELS } from '@/lib/rules'
import { useUserNow } from '@/lib/session'
import { isPersonalItem } from '@/lib/tags'
import { callTool } from '@/lib/tools'
import { cn } from '@/lib/utils'
import { HeadsUpPanel } from './heads-up'
import { EstimateSelect, PlanText } from './item-plan'
import { TagsField } from './tags-field'
import { useWorkspace } from './workspace'

const CONFIDENCE_LABELS: Record<SuggestionConfidence, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
}

/**
 * hugo's suggestion for an inbox item: where it goes (and by when), why, and how sure hugo is.
 * Accept files it there (undoable, like A in the list); Dismiss keeps it in the inbox.
 */
function SuggestionPanel({
  item,
  suggestion,
  timeZone,
}: {
  item: Item
  suggestion: ItemSuggestion
  timeZone: string
}) {
  const actions = useItemActions()
  const [busy, setBusy] = useState(false)
  const act = async (fn: (it: Item) => Promise<unknown>) => {
    setBusy(true)
    await fn(item)
    setBusy(false)
  }
  const due = suggestion.dueDate ? formatDueDate(suggestion.dueDate, timeZone) : null
  return (
    <section
      aria-labelledby="item-suggestion-heading"
      className="flex flex-col gap-2 rounded-lg border p-3"
      data-testid="suggestion-panel"
      data-confidence={suggestion.confidence}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="item-suggestion-heading" className="flex items-center gap-1.5 text-sm font-medium">
          <SparklesIcon aria-hidden="true" className="size-4" />
          hugo suggests
        </h3>
        <Badge
          variant="outline"
          className={suggestion.confidence === 'high' ? undefined : 'text-muted-foreground'}
          data-testid="suggestion-confidence"
        >
          {CONFIDENCE_LABELS[suggestion.confidence]}
        </Badge>
      </div>
      <p className="text-sm font-medium" data-testid="suggestion-choice">
        {choiceLabel(suggestion.choice)}
        {due ? <span className="font-normal text-muted-foreground"> · by {due}</span> : null}
        {suggestion.priority !== 'normal' ? (
          <span className="font-normal text-muted-foreground">
            {' '}
            · {priorityLabel(suggestion.priority)} priority
          </span>
        ) : null}
      </p>
      {suggestion.reason ? (
        <p className="text-sm text-muted-foreground" data-testid="suggestion-reason">
          {suggestion.reason}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => void act(actions.accept)}>
          <CheckIcon aria-hidden="true" />
          Accept
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => void act(actions.dismissSuggestion)}
        >
          <XIcon aria-hidden="true" />
          Dismiss
        </Button>
      </div>
    </section>
  )
}

/**
 * An item hugo filed by itself. While that filing can still be undone (nothing changed the item
 * since, see undoableHugoFiling), "Undo hugo's filing" puts it back in the inbox. Otherwise the
 * user moves it like any item.
 */
function FiledByHugoPanel({ history }: { history: ItemHistoryEntry[] }) {
  const { undo } = useRunTool()
  const [busy, setBusy] = useState(false)
  const actionId = undoableHugoFiling(history)
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2"
      data-testid="filed-by-hugo-panel"
    >
      <p className="flex items-center gap-1.5 text-sm">
        <SparklesIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        Filed by hugo
      </p>
      {actionId ? (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            await undo(actionId)
            setBusy(false)
          }}
        >
          <Undo2Icon aria-hidden="true" />
          Undo hugo's filing
        </Button>
      ) : null}
    </div>
  )
}

const AI_UNAVAILABLE = "hugo's AI is not available right now"
const AI_BUDGET_USED = "Today's AI budget is used up"

/** Why hugo could not draft: the API's words when it is unavailable (e.g. off), else ours. */
function draftErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Could not reach hugo. Please try again.'
  if (error.status === 429) return AI_BUDGET_USED
  if (error.status === 503) return error.code === 'http_error' ? AI_UNAVAILABLE : error.message
  return error.message
}

/**
 * A reply in the item's thread. It goes to the Outbox; nothing is sent until it is approved there.
 * ⌘↵ / Ctrl+↵ adds it. "Draft with hugo" (Enter in the instruction field) asks hugo's AI for the
 * text: it fills an empty box, or replaces the text after asking. A draft is never sent by
 * itself: "Add to Outbox" stays the only way out.
 */
function ReplyForm({ item }: { item: Item }) {
  const { run } = useRunTool()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { openDetails } = useWorkspace()
  const ai = useAiStatus()
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [instruction, setInstruction] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  /** The text came from hugo: a note asks to check it. Gone with the text. */
  const [drafted, setDrafted] = useState(false)
  /** A draft that waits for "Replace your text?". */
  const [pendingDraft, setPendingDraft] = useState<string | null>(null)
  /** The text as it is now, for a draft that arrives after the user typed meanwhile. */
  const bodyNow = useRef(body)
  useEffect(() => {
    bodyNow.current = body
  }, [body])
  const text = body.trim()
  // Offered unless hugo's AI is known to be off here; a failure says why inline.
  const canDraft = ai.data?.enabled !== false
  // GitHub answers are comments in the issue's thread, addressed to nobody.
  const comment = item.source === 'github'

  const edit = (next: string) => {
    setBody(next)
    if (!next.trim()) setDrafted(false)
  }

  const applyDraft = (draft: string) => {
    setBody(draft)
    setDrafted(true)
  }

  const draft = async () => {
    if (drafting) return
    setDrafting(true)
    setDraftError(null)
    try {
      const { result } = await callTool('item.draft_reply', {
        id: item.id,
        instruction: instruction.trim() || null,
      })
      const draftText = result.body.slice(0, REPLY_BODY_MAX)
      if (bodyNow.current.trim()) setPendingDraft(draftText)
      else applyDraft(draftText)
    } catch (error) {
      const message = draftErrorText(error)
      log.warn('item.draft_reply failed', message)
      setDraftError(message)
    } finally {
      setDrafting(false)
      // A draft costs: Settings shows today's AI cost.
      void queryClient.invalidateQueries({ queryKey: queryKeys.ai })
    }
  }

  const submit = async () => {
    if (!text || saving) return
    setSaving(true)
    const result = await run(
      'item.reply',
      { id: item.id, body: text },
      {
        done: comment ? 'Comment waiting for approval' : 'Reply waiting for approval',
        doneAction: {
          label: 'Open Outbox',
          onClick: () => {
            openDetails(null)
            void navigate({ to: '/outbox' })
          },
        },
        failed: 'Could not add the reply',
      },
    )
    setSaving(false)
    if (result) edit('')
  }

  return (
    <section aria-label="Reply" className="flex flex-col gap-1.5">
      <Label htmlFor="item-reply">
        {comment ? 'Comment on GitHub' : `Reply to ${item.counterpart?.name ?? 'sender'}`}
      </Label>
      {canDraft ? (
        <div className="flex flex-col gap-1.5" data-testid="reply-draft">
          <Input
            id="item-reply-instruction"
            aria-label="What hugo should say (optional)"
            placeholder="Say what to answer, e.g. I'll send it Friday"
            value={instruction}
            maxLength={AI_INSTRUCTION_MAX}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void draft()
              }
            }}
          />
          <div className="flex">
            <Button variant="outline" onClick={() => void draft()} disabled={drafting}>
              {drafting ? <Spinner aria-hidden="true" /> : <SparklesIcon aria-hidden="true" />}
              Draft with hugo
            </Button>
          </div>
          {draftError ? (
            <p role="alert" className="text-sm text-destructive" data-testid="draft-error">
              {draftError}
            </p>
          ) : null}
        </div>
      ) : null}
      <Textarea
        id="item-reply"
        rows={drafted ? 7 : 4}
        value={body}
        maxLength={REPLY_BODY_MAX}
        placeholder="Write your reply…"
        aria-describedby={drafted ? 'item-reply-drafted item-reply-hint' : 'item-reply-hint'}
        onChange={(e) => edit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void submit()
          }
        }}
      />
      {drafted ? (
        <p
          id="item-reply-drafted"
          className="flex items-start gap-1.5 text-xs text-muted-foreground"
          data-testid="draft-note"
        >
          <SparklesIcon aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          Drafted by hugo. Check it before you add it to the Outbox.
        </p>
      ) : null}
      <p id="item-reply-hint" className="text-xs text-muted-foreground">
        It waits in the Outbox until you approve it.
      </p>
      <div className="flex">
        <Button onClick={() => void submit()} disabled={!text || saving}>
          {saving ? <Spinner aria-hidden="true" /> : <SendIcon aria-hidden="true" />}
          Add to Outbox
        </Button>
      </div>
      <AlertDialog
        open={pendingDraft !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDraft(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your text?</AlertDialogTitle>
            <AlertDialogDescription>
              hugo's draft takes the place of what you wrote in the reply box.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my text</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDraft !== null) applyDraft(pendingDraft)
                setPendingDraft(null)
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

/**
 * A text field that saves on blur. It takes updates from the server (other tabs, other fields'
 * saves) only while you are not editing it, so a save elsewhere never wipes what you type.
 */
function useEditable(serverValue: string) {
  const [value, setValue] = useState(serverValue)
  const editing = useRef(false)
  useEffect(() => {
    if (!editing.current) setValue(serverValue)
  }, [serverValue])
  return {
    value,
    setValue,
    onFocus: () => {
      editing.current = true
    },
    done: () => {
      editing.current = false
    },
  }
}

/** One property of the item: a short label beside its control (wraps under it on narrow sheets). */
function Property({
  label,
  htmlFor,
  id,
  children,
}: {
  label: string
  htmlFor?: string
  id?: string
  children: ReactNode
}) {
  return (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1">
      <Label htmlFor={htmlFor} id={id} className="text-muted-foreground">
        {label}
      </Label>
      <div className="flex min-w-0 items-center gap-1.5">{children}</div>
    </div>
  )
}

/**
 * The item's details, the essentials first: its title, where it is (list), who is on the other
 * side, its date, the time it needs and hugo's plan. Notes show once there are some (or the user
 * adds them); the reply box opens with Reply; the type and the history are under More.
 */
function ItemForm({
  item,
  history,
  sources,
  timeZone,
}: {
  item: Item
  history: ItemHistoryEntry[]
  sources: ItemSourceLink[]
  timeZone: string
}) {
  const { run } = useRunTool()
  const actions = useItemActions()
  const now = useUserNow()
  const titleField = useEditable(item.title)
  const personField = useEditable(item.counterpart?.name ?? '')
  const notesField = useEditable(item.notes ?? '')
  const title = titleField.value
  const person = personField.value
  const notes = notesField.value
  const [notesOpen, setNotesOpen] = useState(false)
  const [replyOpen, setReplyOpen] = useState(false)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const people = useQuery({
    queryKey: ['people'],
    queryFn: async () => (await callTool('people.search', { q: '', limit: 50 })).result.people,
    staleTime: 60_000,
  })
  const closed = item.status === 'done' || item.status === 'dropped'
  /** Open tasks and promises get a time needed and hugo's plan. */
  const planned =
    (item.kind === 'task' || item.kind === 'owe') &&
    (item.status === 'open' || item.status === 'snoozed')
  const links = sources.filter(
    (source): source is ItemSourceLink & { url: string } => source.url !== null,
  )
  const canReply = sources.some((source) => source.canReply)
  const personal = isPersonalItem(item, useSettings().data?.personalTags)
  const showNotes = notesOpen || notes !== '' || (item.notes ?? '') !== ''

  useEffect(() => {
    if (notesOpen) notesRef.current?.focus()
  }, [notesOpen])

  const saveTitle = () => {
    titleField.done()
    const next = title.trim()
    if (next && next !== item.title) void run('item.update', { id: item.id, title: next })
    else titleField.setValue(item.title)
  }
  const savePerson = () => {
    personField.done()
    const next = person.trim()
    if (next !== (item.counterpart?.name ?? '')) {
      void run(
        'item.set_counterpart',
        { id: item.id, name: next || null },
        { done: next ? `Person: ${next}` : 'Person removed' },
      )
    }
  }
  const saveNotes = () => {
    notesField.done()
    if (notes !== (item.notes ?? '')) void run('item.update', { id: item.id, notes: notes || null })
    // An empty field stays while the sheet is open: closing it on blur moved everything below it
    // up under the pointer, so the next click missed (found in the browser test).
  }

  return (
    <div className="flex flex-col gap-4 overflow-y-auto px-4 pb-[calc(var(--safe-bottom)+1rem)]">
      <Textarea
        id="item-title"
        aria-label="Title"
        rows={1}
        value={title}
        maxLength={TITLE_MAX}
        onFocus={titleField.onFocus}
        onChange={(e) => titleField.setValue(e.target.value.replace(/\n/g, ' '))}
        onBlur={saveTitle}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        className="-mx-2.5 -my-1 min-h-0 resize-none border-transparent bg-transparent px-2.5 py-1 font-heading text-lg leading-snug font-semibold shadow-none hover:border-input md:text-lg dark:bg-transparent"
      />

      {item.suggestion ? (
        <SuggestionPanel item={item} suggestion={item.suggestion} timeZone={timeZone} />
      ) : null}
      {item.filedBy === 'agent' && item.status !== 'inbox' ? (
        <FiledByHugoPanel history={history} />
      ) : null}
      {item.headsUp ? (
        <HeadsUpPanel key={item.headsUp.episode} headsUp={item.headsUp} timeZone={timeZone} />
      ) : null}

      <div className="flex flex-col gap-2.5" data-testid="item-properties">
        {item.kind !== 'fyi' && !closed ? (
          // Who waits on it: nobody (a task), someone on the user (I owe), or the user on someone
          // (waiting on). A property of the item, not a list (decision 96); in the inbox, choosing
          // one files the item.
          <Property label="Waiting" id="item-waiting-label">
            <ToggleGroup
              aria-labelledby="item-waiting-label"
              variant="outline"
              size="sm"
              spacing={1}
              className="flex-wrap"
              data-testid="item-waiting"
              value={item.kind && item.status !== 'inbox' ? [item.kind] : []}
              onValueChange={(value) => {
                const kind = value[0] as WaitingKind | undefined
                if (kind) void actions.move(item, kind)
              }}
            >
              {WAITING_CHOICES.map((choice) => (
                <ToggleGroupItem key={choice.kind} value={choice.kind} title={choice.hint}>
                  {choice.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Property>
        ) : null}
        <Property
          label={
            item.kind === 'owe' ? 'Owed to' : item.kind === 'waiting' ? 'Waiting on' : 'Person'
          }
          htmlFor="item-person"
        >
          <Input
            id="item-person"
            list="people-options"
            placeholder="Name"
            className="h-8"
            value={person}
            onFocus={personField.onFocus}
            onChange={(e) => personField.setValue(e.target.value)}
            onBlur={savePerson}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
          <datalist id="people-options">
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.name} />
            ))}
          </datalist>
        </Property>
        <Property
          label={
            item.kind === 'owe' ? 'Promised by' : item.kind === 'waiting' ? 'Follow up on' : 'Due'
          }
          htmlFor="item-due"
        >
          <Input
            id="item-due"
            type="date"
            className="h-8"
            value={item.dueAt ? localDateInput(item.dueAt, timeZone) : ''}
            onChange={(e) =>
              void run(
                'item.set_due',
                { id: item.id, due: e.target.value ? { date: e.target.value } : null },
                { done: e.target.value ? 'Due date set' : 'Due date cleared' },
              )
            }
          />
          {item.dueAt ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                void run('item.set_due', { id: item.id, due: null }, { done: 'Due date cleared' })
              }
            >
              Clear
            </Button>
          ) : null}
        </Property>
        {!closed && item.kind !== 'fyi' && item.kind !== 'waiting' ? (
          <Property label="Priority" id="item-priority-label">
            <div className="flex flex-col items-start gap-1">
              <ToggleGroup
                aria-labelledby="item-priority-label"
                variant="outline"
                size="sm"
                spacing={1}
                className="flex-wrap"
                data-testid="item-priority"
                value={[item.priority]}
                onValueChange={(value) => {
                  const priority = value[0] as Item['priority'] | undefined
                  // Choosing what a rule gave makes it the user's own.
                  if (priority && (priority !== item.priority || !item.prioritySet)) {
                    void actions.setPriority(item, priority)
                  }
                }}
              >
                {PRIORITY_CHOICES.map((choice) => (
                  <ToggleGroupItem key={choice.value} value={choice.value} title={choice.hint}>
                    {choice.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <PriorityOrigin item={item} onReset={() => void actions.setPriority(item, null)} />
            </div>
          </Property>
        ) : null}
        {closed ? null : (
          <Property label="Tags" htmlFor="item-tags">
            <TagsField item={item} id="item-tags" />
          </Property>
        )}
        {planned ? (
          <section aria-label="Plan" className="flex flex-col gap-2" data-testid="item-plan">
            <Property label="Time needed" htmlFor="item-estimate">
              <EstimateSelect item={item} id="item-estimate" />
            </Property>
            <PlanText item={item} timeZone={timeZone} personal={personal} />
          </section>
        ) : null}
      </div>

      {showNotes ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="item-notes" className="text-muted-foreground">
            Notes
          </Label>
          <Textarea
            ref={notesRef}
            id="item-notes"
            rows={3}
            value={notes}
            onFocus={notesField.onFocus}
            onChange={(e) => notesField.setValue(e.target.value)}
            onBlur={saveNotes}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {closed ? (
          <Button variant="outline" onClick={() => void actions.reopen(item)}>
            <RotateCcwIcon aria-hidden="true" />
            Reopen
          </Button>
        ) : (
          <>
            <Button onClick={() => void actions.complete(item)}>
              <CheckIcon aria-hidden="true" />
              Mark done
            </Button>
            <Button variant="outline" onClick={() => void actions.drop(item)}>
              <Trash2Icon aria-hidden="true" />
              Drop
            </Button>
          </>
        )}
        {canReply ? (
          <Button
            variant="outline"
            aria-expanded={replyOpen}
            aria-controls="item-reply-section"
            onClick={() => setReplyOpen((open) => !open)}
          >
            <ReplyIcon aria-hidden="true" />
            Reply
          </Button>
        ) : null}
        {!showNotes ? (
          <Button variant="ghost" onClick={() => setNotesOpen(true)}>
            <NotebookPenIcon aria-hidden="true" />
            Add notes
          </Button>
        ) : null}
        {links.map((source) => (
          <a
            key={`${source.connector}:${source.url}`}
            href={source.url}
            target="_blank"
            rel="noreferrer"
            className={cn(buttonVariants({ variant: 'ghost' }))}
          >
            Open in {connectorTitles[source.connector]}
            <ExternalLinkIcon aria-hidden="true" data-icon="inline-end" />
          </a>
        ))}
      </div>

      {canReply && replyOpen ? (
        <div id="item-reply-section" className="rounded-lg border p-3">
          <ReplyForm item={item} />
        </div>
      ) : null}

      <details className="group" data-testid="item-more">
        <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-md py-1 text-sm font-medium text-muted-foreground outline-none select-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
          <ChevronRightIcon
            aria-hidden="true"
            className="size-4 transition-transform group-open:rotate-90"
          />
          More
          <span className="font-normal">· type, history</span>
        </summary>
        <div className="flex flex-col gap-4 pt-3">
          <Property label="Type" htmlFor="item-type">
            <NativeSelect
              id="item-type"
              size="sm"
              className="w-full"
              value={item.type ?? ''}
              onChange={(e) =>
                void run('item.update', {
                  id: item.id,
                  type: (e.target.value || null) as Item['type'],
                })
              }
            >
              <NativeSelectOption value="">Not set</NativeSelectOption>
              {itemTypes.map((t) => (
                <NativeSelectOption key={t} value={t}>
                  {TYPE_LABELS[t]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Property>
          <section aria-label="History" className="flex flex-col gap-2 pb-2">
            <h3 className="text-sm font-medium">History</h3>
            <ol className="flex flex-col gap-1.5" data-testid="item-history">
              {[...history].reverse().map((entry) => {
                const { label, detail, actor } = historyLine(entry, timeZone)
                return (
                  <li
                    key={entry.seq}
                    className="flex items-baseline justify-between gap-3 text-sm"
                    data-undone={entry.undone}
                  >
                    <span className={entry.undone ? 'text-muted-foreground line-through' : ''}>
                      {label}
                      {detail ? <span className="text-muted-foreground"> · {detail}</span> : null}
                      {actor ? <span className="text-muted-foreground"> · {actor}</span> : null}
                    </span>
                    <time className="shrink-0 text-xs text-muted-foreground" dateTime={entry.at}>
                      {relativeTime(new Date(entry.at), now)}
                    </time>
                  </li>
                )
              })}
            </ol>
          </section>
        </div>
      </details>
    </div>
  )
}

/** Details of the item opened with Enter, a second click, or the Details button. */
export function ItemSheet() {
  const { detailsId, openDetails } = useWorkspace()
  const detail = useItemDetail(detailsId)
  const settings = useSettings()
  const isMobile = useIsMobile()
  const timeZone = settings.data?.timezone ?? 'Europe/Zurich'
  const item = detail.data?.item
  return (
    <Sheet
      open={detailsId !== null}
      onOpenChange={(open) => {
        if (!open) openDetails(null)
      }}
    >
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        // Phones: a bottom sheet that leaves the top of the page visible. Desktop: full height.
        className={cn('w-full gap-4 sm:max-w-md', isMobile && 'max-h-[92svh]')}
        data-testid="item-sheet"
        data-state-ready={detail.isSuccess}
      >
        <SheetHeader className="pr-10 pb-0">
          {/* The title is the first field below, where it can be edited. */}
          <SheetTitle className="sr-only">{item?.title ?? 'Item'}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-1.5">
            {item ? (
              <>
                <Badge variant="secondary" data-testid="item-state">
                  {itemStateLabel(item)}
                </Badge>
                <span>{sourceLabel(item.source)}</span>
                {item.status === 'snoozed' && item.snoozedUntil ? (
                  <span>· snoozed until {formatDue(item.snoozedUntil, false, timeZone)}</span>
                ) : null}
              </>
            ) : null}
          </SheetDescription>
        </SheetHeader>
        {item && detail.data ? (
          <ItemForm
            key={item.id}
            item={item}
            history={detail.data.history}
            sources={detail.data.sources}
            timeZone={timeZone}
          />
        ) : detail.isError ? (
          <div className="px-4">
            <Alert variant="destructive">
              <AlertTitle>Could not load this item</AlertTitle>
              <AlertDescription>
                {detail.error instanceof ApiError
                  ? detail.error.message
                  : 'Something went wrong. Please try again.'}
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-4">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

/**
 * Under the priority: which of the user's rules set it ("From your rule #client"), or, when the
 * user set it, a way to hand it back to the rules.
 */
function PriorityOrigin({ item, onReset }: { item: Item; onReset: () => void }) {
  const settings = useSettings()
  const rule = ruleAt(settings.data?.rules, item.priorityRuleIndex)
  if (item.prioritySet) {
    if (!settings.data?.rules.priorityRules.length) return null
    return (
      <Button
        variant="link"
        size="sm"
        className="h-auto px-0 text-xs text-muted-foreground"
        data-testid="priority-reset"
        onClick={onReset}
      >
        Set by you · let your rules decide
      </Button>
    )
  }
  if (!rule) return null
  return (
    <p className="text-xs text-muted-foreground" data-testid="priority-origin">
      From your rule {conditionLabel(rule.when)}
    </p>
  )
}
